import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Download, FileQuestion, Loader2 } from 'lucide-react';
import { fetchAdminDocument } from 'redux/services/documentDownloads';
import ImageView from './ImageView';
import styles from './document-viewer.module.css';

const PdfPages = lazy(() => import('./PdfPages'));
const DocxView = lazy(() => import('./DocxView'));

const OPEN_FAILED = 'This file could not be opened — it may have been removed or replaced.';

// Which renderer shows a file, by the type the download route reports. Adding a
// format later (docx, xlsx) is one entry here plus its renderer — nothing else
// in the viewer changes.
const rendererFor = (mimeType = '') => {
  const type = mimeType.toLowerCase();
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx';
  return 'unsupported';
};

const Loading = () => (
  <div className={styles.state} role="status">
    <Loader2 className={styles.spinner} aria-hidden="true" />
    <span>Loading document…</span>
  </div>
);

const Message = ({ children }) => (
  <div className={styles.state}>
    <FileQuestion className={styles.stateIcon} aria-hidden="true" />
    <span>{children}</span>
  </div>
);

// The file, fetched through a permission- and tenant-checked download route.
// Shared by the pop-up viewer and the viewer's own tab, so both open a file
// the same way. Returns the state and a way to mark it failed, for a renderer
// that finds the file unreadable after it arrived.
//
// `fetchFile` is the route to fetch through, defaulting to the lender's. The
// borrower's portal passes its own: the two downloads check different things
// — one that the caller is staff on the loan, the other that the caller is a
// borrower on it — and a viewer hard-wired to the lender's would 403 for
// every borrower.
export const useViewerFile = (documentId, active, fetchFile = fetchAdminDocument) => {
  const [state, setState] = useState({ status: 'idle' });

  useEffect(() => {
    if (!active || !documentId) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setState({ status: 'loading' });

    fetchFile({ documentId })
      .then((blob) => {
        if (cancelled) return;
        const kind = rendererFor(blob.type);
        if (kind === 'image') objectUrl = URL.createObjectURL(blob);
        setState({ status: 'ready', kind, blob, url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [active, documentId, fetchFile]);

  const fail = useCallback(() => setState({ status: 'error' }), []);
  return [state, fail];
};

/* A type the viewer cannot draw (.doc, spreadsheets, anything else) offers the
   file itself instead, so it is never stuck unopenable. The only download in
   the viewer: a file it can show stays view only. */
const DownloadFallback = ({ blob, name }) => {
  const handleDownload = () => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoked a moment later: Firefox cancels a download whose URL is revoked
    // in the same tick as the click.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className={styles.state}>
      <FileQuestion className={styles.stateIcon} aria-hidden="true" />
      <span>Preview isn’t available for this file type.</span>
      <button type="button" className={styles.downloadButton} onClick={handleDownload}>
        <Download aria-hidden="true" />
        Download to open it
      </button>
    </div>
  );
};

// View only — no download or print — for anything it can show.
export const ViewerBody = ({ state, onError, name }) => {
  if (state.status === 'error') return <Message>{OPEN_FAILED}</Message>;
  if (state.status !== 'ready') return <Loading />;
  if (state.kind === 'pdf') {
    return (
      <Suspense fallback={<Loading />}>
        <PdfPages file={state.blob} loading={<Loading />} onError={onError} />
      </Suspense>
    );
  }
  if (state.kind === 'image') return <ImageView src={state.url} />;
  if (state.kind === 'docx') {
    return (
      <Suspense fallback={<Loading />}>
        <DocxView file={state.blob} onError={onError} />
      </Suspense>
    );
  }
  return <DownloadFallback blob={state.blob} name={name} />;
};

// The chat passes its link's children as the title — a string, or the pieces
// markdown split it into. The tab's link needs it as plain text.
export const textOf = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  return textOf(node.props?.children);
};

// Where the viewer's own tab lives. The name rides along in the link because
// the download hands back the file alone, and the tab has nothing else to
// title itself with.
export const documentPagePath = (documentId, name) => {
  const query = name ? `?name=${encodeURIComponent(name)}` : '';
  return `/document/${encodeURIComponent(documentId)}${query}`;
};
